import 'leaflet/dist/leaflet.css';

import L from 'leaflet';
import React, { useEffect, useMemo } from 'react';
import {
  MapContainer,
  Marker,
  TileLayer,
  useMap,
  useMapEvents,
} from 'react-leaflet';

import {
  CARTO_TILES_ATTRIBUTION,
  CARTO_TILES_URL,
} from '../../../utils/map-tiles.util';

// Centre de la France, utilisé tant que le destinataire n'a pas de coordonnées
const DEFAULT_CENTER = [46.6, 2.4];
const DEFAULT_ZOOM = 5;
const POSITION_ZOOM = 16;
// En dessous de ce zoom, la vue est trop large pour ajuster la position : on recentre sur le point
const MIN_DETAIL_ZOOM = 12;

const RECIPIENT_ICON = L.divIcon({
  html: '<div class="rf-marker"></div>',
  className: 'rf-marker-wrapper',
  iconSize: [22, 22],
  iconAnchor: [11, 26],
});

/**
 * Recentre la carte quand la position (mémoïsée par le parent) change depuis le formulaire
 * et recalcule sa taille une fois la modale affichée
 */
const MapController = ({ position }) => {
  const map = useMap();

  useEffect(() => {
    const timeout = setTimeout(() => map.invalidateSize(), 250);
    return () => clearTimeout(timeout);
  }, [map]);

  useEffect(() => {
    // Après un glisser-déposer, le point reste visible : on ne bouge pas la vue choisie par l'utilisateur
    if (
      position &&
      (!map.getBounds().contains(position) || map.getZoom() < MIN_DETAIL_ZOOM)
    ) {
      map.flyTo(position, Math.max(map.getZoom(), POSITION_ZOOM), {
        duration: 0.5,
      });
    }
  }, [map, position]);

  return null;
};

// Un clic sur la carte place (ou déplace) le destinataire
const MapClickHandler = ({ onChange, disabled }) => {
  useMapEvents({
    click: (event) => {
      if (!disabled) {
        onChange(event.latlng.lat, event.latlng.lng);
      }
    },
  });

  return null;
};

/**
 * Carte zoomable du destinataire : le marqueur se déplace par glisser-déposer (ou par un clic sur la
 * carte) pour ajuster la position de livraison
 * @param {number|null} latitude
 * @param {number|null} longitude
 * @param {Function} onChange - (latitude, longitude) => void
 * @param {boolean} disabled
 */
export default function RecipientLocationMap({
  latitude,
  longitude,
  onChange,
  disabled,
}) {
  const position = useMemo(
    () =>
      Number.isFinite(latitude) && Number.isFinite(longitude)
        ? [latitude, longitude]
        : null,
    [latitude, longitude]
  );

  const markerEventHandlers = useMemo(
    () => ({
      dragend: (event) => {
        const { lat, lng } = event.target.getLatLng();
        onChange(lat, lng);
      },
    }),
    [onChange]
  );

  return (
    <div className="rf-map">
      <MapContainer
        center={position || DEFAULT_CENTER}
        zoom={position ? POSITION_ZOOM : DEFAULT_ZOOM}
        scrollWheelZoom
        className="rf-map__leaflet"
      >
        <TileLayer
          attribution={CARTO_TILES_ATTRIBUTION}
          url={CARTO_TILES_URL}
        />
        <MapController position={position} />
        <MapClickHandler onChange={onChange} disabled={disabled} />
        {position && (
          <Marker
            position={position}
            icon={RECIPIENT_ICON}
            draggable={!disabled}
            eventHandlers={markerEventHandlers}
          />
        )}
      </MapContainer>
    </div>
  );
}
